import { useCallback, useContext, useState, useEffect } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import TreeLoading from "../Trees/TreeLoading";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import ExpandCollapse from "../Search/ExpandCollapse";
import SearchButton from "../Search/SearchButton";
import WorkflowTemplateTree from "../Trees/WorkflowTemplateTree";
import WorkflowTemplateOptionsMenu from "../RightContextMenu/WorkflowTemplateOptionsMenu";
import { FIELD_TYPE, OTHER_TYPE, RECORD_TYPE } from "../../constants/datatypes";
import AcceleratorWorkflowWizard from "../Repository/AcceleratorWizard/AcceleratorWorkflowWizard";
import { treeActions } from "../../redux/slices/treeSlice";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

export default function WorkflowTemplatePanel() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore();
  const [disabled, setDisabled] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [showAcceleratorIcon, setShowAcceleratorIcon] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configDialogData, setConfigDialogData] = useState(null);
  const [hasAcceleratorPermission, setHasAcceleratorPermission] = useState(false);
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree, leftTree } = useSelector((state) => state.treeData?.[parentScreen] ?? {});
  const pendingAcceleratorConfig = useSelector((state) => state.treeData?.pendingAcceleratorConfigDialog);
  const { permissions } = useSelector((state) => state.userPermissions);
  const [isCopied,setIsCopied] = useState({ copy: false, paste: false })
  const [expandActions,setExpandActions] = useState({tab:'',keys:[]});
  const leftSelectedNode = leftTree.tree.body.selectedNode;
  const templateHeader = rightTree.tree.templateHeader;
  const [deleteErrorModal,setDeleteErrorModal] = useState(false);
  const [deleteErrorMessage,setDeleteErrorMessage] = useState('')
  const rightSelectedNode = rightTree?.tree?.body?.selectedNode;
  const content = t('LBL_NO_HEADER_TRAILER_ALLOWED').replace('{0}', templateHeader?.FILETYPE).split('\\n').map((str, idx) => <div key={idx}>{str ? str : <br />}</div>);

  useEffect(() => {
    if (permissions?.length > 0) {
      if (permissions.includes("ACCELERATOR_RIGHTS")) {
        setHasAcceleratorPermission(true);
      } else {
        setHasAcceleratorPermission(false);
      }
    } else {
      setHasAcceleratorPermission(false);
    }
  }, [permissions]);

  useEffect(() => {
    setDisabled(leftSelectedNode?.type !== 'workflow');
    if (hasAcceleratorPermission && leftSelectedNode?.type === 'workflow' && templateHeader?.WORKFLOW?.COMMENTS?.toLowerCase().includes('accelerator')) {
      setShowAcceleratorIcon(true);
    } else {
      setShowAcceleratorIcon(false);
    }
  }, [leftSelectedNode?.key, templateHeader?.WORKFLOW?.COMMENTS, hasAcceleratorPermission]);

  useEffect(() => {
    if (parentScreen !== 'workflow' || !pendingAcceleratorConfig) return;
    if (rightTree.loading || !rightTree.path) return;

    const templateName = typeof pendingAcceleratorConfig.template === 'object' ? pendingAcceleratorConfig.template?.name || pendingAcceleratorConfig.template?.title : pendingAcceleratorConfig.template;
    const expectedPath = `${pendingAcceleratorConfig.workflowGroup}/${templateName}`;
    const pathMatch = rightTree.path === expectedPath || rightTree.path?.startsWith(pendingAcceleratorConfig.workflowGroup + '/');
    if (pathMatch) {
      setConfigDialogData(pendingAcceleratorConfig);
      setShowConfigDialog(true);
      dispatch(treeActions.clearPendingAcceleratorConfigDialog());
    }
  }, [parentScreen, pendingAcceleratorConfig, rightTree.loading, rightTree.path, dispatch]);

  const handleProceedWithConfig = () => {
    setShowConfigDialog(false);
    if (configDialogData?.workflowGroup && Array.isArray(configDialogData.workflows) && configDialogData.workflows.length > 0) {
      const sorted = [...configDialogData.workflows].sort((a, b) => {
        const nameA = typeof a === 'string' ? a : (a?.name ?? a?.title ?? '');
        const nameB = typeof b === 'string' ? b : (b?.name ?? b?.title ?? '');
        return String(nameA).localeCompare(String(nameB), undefined, { sensitivity: 'base' });
      });
      dispatch(treeActions.setLeftTreeContextActionValue({ screen: 'workflow', data: false }));
      dispatch(treeActions.setAcceleratorWorkflowQueue({
        workflowGroup: configDialogData.workflowGroup,
        workflows: sorted,
        currentIndex: 0
      }));
    }
    setConfigDialogData(null);
    setWizardOpen(true);
  };

  const handleSkipConfig = () => {
    setShowConfigDialog(false);
    if (configDialogData) {
      const { workflowGroup, template } = configDialogData;
      dispatch(treeActions.setCurrentScreen('workflow'));
      dispatch(treeActions.setForceTemplate({
        workflowGroup,
        template,
        forceSelect: true,
        screen: 'workflow'
      }));
      let retryCount = 0;
      const maxRetries = 10;
      const selectFirstItem = () => {
        const state = store.getState();
        const rt = state.treeData?.workflow?.rightTree;
        const translatedData = rt?.tree?.body?.translatedData;
        if (translatedData?.length > 0) {
          dispatch(treeActions.setRightTreeSelectedNode({
            screen: 'workflow',
            type: 'body',
            data: translatedData[0]
          }));
        } else if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(selectFirstItem, 200);
        }
      };
      setTimeout(selectFirstItem, 300);
    }
    setConfigDialogData(null);
  };

  const handleKeyDown = useCallback((event)=>{
    if (event.ctrlKey && rightSelectedNode) {
      if (event.key === "c" || event.key === "C") {
        setIsCopied({...isCopied, paste: false, copy: true,treeType:parentScreen });
      } else if (event.key === "v" || event.key === "V") {
        setIsCopied({ ...isCopied,paste:true,dispatchFrom:'keyPress',treeType:parentScreen});
      }
    } 
  },[isCopied, rightSelectedNode, parentScreen]);



  return (
    <div
      id="wf-tree"
      onKeyDown={(e)=>{
        if (document?.activeElement?.id !== "wf-tree") return;
        handleKeyDown(e, isCopied)
      }} 
      tabIndex={0} 
      className="flex-column height-100"
    >
      
      <div
        style={{
          height: "32px",
          borderBottom: "1px solid #e0e0e0",
          width: "100%",
          display: "flex",
          justifyContent: "space-between"
        }}>
        <Button
          size="small"
          className="left-tree-tab-button"
          sx={{ cursor: "unset", pointerEvents: "none", display: "flex", alignItems: "center" }}
        >
          <>
            {/* <CategoryIcon fontSize="small" className="margin-right-4" /> */}
            {t("TIT_WORKFLOW_ITEMS")}
          </>
        </Button>
        <div style={{display: "flex", alignItems: "center", borderLeft: "1px solid #e0e0e0", paddingLeft: "8px" }}>
          {showAcceleratorIcon && <IconButton 
            size="small" 
            onClick={() => setWizardOpen(true)}
            variant="outlined"
            sx={{
              borderRadius: "0 !important",
            }}
          >
            <span className="rep-accelerator-wizard-mini"></span>
          </IconButton>}
          <SearchButton setExpandActions={setExpandActions} />
          <ExpandCollapse setExpandActions={setExpandActions} />
        </div>
      </div>
      <Box sx={{flex: "1", overflow: "auto"}}  id="rightTreeWorkflow">

        {/* body */}
        <div className={"height-100"}>
          <div className="height-100 overflow-auto">
            {
              rightTree.tree.body.translatedData?.length > 0 ?
                <WorkflowTemplateTree expandActions={expandActions} tab="body" />
                : rightTree.loading ? <TreeLoading />
                  : rightTree.fetchError ? <div className="panel-fetch-error">{t('ERROR_FETCHING_TEMPLATE')}</div>
                    : leftTree?.tree?.body?.selectedNode?.type === "workflow" ? <div className="panel-no-data"></div>
                      : <div className="panel-no-data">{t("LBL_NO_WORKFLOW_ITEM_SELECTED")}</div>
            }
          </div>
        </div>

        {/* {contextMenuOptions} */}
        {(Object.keys(leftSelectedNode).length > 0 && leftSelectedNode?.type === 'workflow') && <WorkflowTemplateOptionsMenu isCopied={isCopied} setIsCopied={setIsCopied} targetId={"rightTreeWorkflow"} />}
      </Box>
      
      <Dialog
        open={showConfigDialog}
        onClose={handleSkipConfig}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)', overflow: 'hidden' } }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2.5,
            px: 3.5,
            backgroundColor: '#F8F8F8',
            borderBottom: '1px solid #e8e8e8',
            '&.MuiDialogTitle-root': { flex: 1 }
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', pr: 2 }}>
            {t('ACCELERATOR_PROCEED_CONFIG_TIT')}
          </Typography>
          <IconButton onClick={handleSkipConfig} size="small" sx={{ flexShrink: 0, '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ marginTop: '28px !important', pt: 4.5, pb: 3.5, px: 3.5, backgroundColor: '#ffffff' }}>
          <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
            <Box sx={{ flexShrink: 0, backgroundColor: '#1976d2', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AutoAwesomeIcon sx={{ color: 'white', fontSize: 26 }} />
            </Box>
            <Typography variant="body1" sx={{ color: '#1a1a1a', lineHeight: 1.5 }}>
              {t('ACCELERATOR_PROCEED_CONFIG_MSG')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 2.5, px: 3.5, pt: 2, backgroundColor: '#F8F8F8', borderTop: '1px solid #e8e8e8', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={handleProceedWithConfig}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, py: 1.25, borderRadius: '6px', backgroundColor: 'white', color: '#1a1a1a', borderColor: '#d0d0d0', '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#b0b0b0' } }}
          >
            {t('ACCELERATOR_BTN_YES_PROCEED')}
          </Button>
          <Button
            onClick={handleSkipConfig}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, py: 1.25, borderRadius: '6px', backgroundColor: '#555', color: 'white', '&:hover': { backgroundColor: '#444' } }}
          >
            {t('ACCELERATOR_BTN_SKIP_LATER')}
          </Button>
        </DialogActions>
      </Dialog>

      <AcceleratorWorkflowWizard 
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
      />
    </div>
  );
}
